import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import useEmblaCarousel from 'embla-carousel-react';

const heroSlides = [
  {
    image: 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Global Shipping Excellence',
    subtitle: 'Delivering Across Five Continents Since 1975',
    description: 'Trusted by businesses worldwide for reliable, efficient maritime logistics.',
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/gxmwasis_step-bg.jpg.png',
    title: 'Modern Fleet, Timeless Reliability',
    subtitle: '50 Years of Maritime Expertise',
    description: 'State-of-the-art vessels equipped for any cargo, any destination.',
  },
  {
    image: 'https://images.pexels.com/photos/2226458/pexels-photo-2226458.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Sustainable Shipping Solutions',
    subtitle: 'Leading the Green Maritime Revolution',
    description: 'Committed to reducing our carbon footprint for future generations.',
  },
];

export const HeroCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [trackingId, setTrackingId] = useState('');

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    // Auto-scroll
    const autoplay = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 6000);

    return () => {
      clearInterval(autoplay);
    };
  }, [emblaApi, onSelect]);

  return (
    <section className="relative h-screen min-h-[700px] overflow-hidden" data-testid="hero-carousel">
      {/* Carousel */}
      <div className="absolute inset-0" ref={emblaRef}>
        <div className="flex h-full">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className="relative flex-[0_0_100%] min-w-0 h-full"
              data-testid={`hero-slide-${index}`}
            >
              {/* Background Image */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${slide.image})` }}
              />
              {/* Overlay */}
              <div className="absolute inset-0 hero-overlay" />
              
              {/* Content */}
              <div className="relative h-full rsl-container flex flex-col justify-center z-10">
                <div className="max-w-3xl">
                  <span className="inline-block text-safety-orange font-mono text-xs tracking-widest uppercase mb-4 animate-fadeInUp">
                    {slide.subtitle}
                  </span>
                  <h1
                    className="text-4xl sm:text-5xl lg:text-7xl font-black text-white uppercase tracking-tight leading-none mb-6 animate-fadeInUp"
                    style={{ animationDelay: '0.1s' }}
                  >
                    {slide.title}
                  </h1>
                  <p
                    className="text-lg text-white/80 max-w-xl mb-8 animate-fadeInUp"
                    style={{ animationDelay: '0.2s' }}
                  >
                    {slide.description}
                  </p>
                  <div
                    className="flex flex-wrap gap-4 animate-fadeInUp"
                    style={{ animationDelay: '0.3s' }}
                  >
                    <Link to="/quote">
                      <Button
                        className="cta-primary h-14 px-8"
                        data-testid="hero-get-quote-btn"
                      >
                        Get Free Quote
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/services">
                      <Button
                        variant="outline"
                        className="cta-secondary h-14 px-8"
                        data-testid="hero-services-btn"
                      >
                        Our Services
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={scrollPrev}
        className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur hover:bg-safety-orange transition-colors"
        data-testid="carousel-prev"
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur hover:bg-safety-orange transition-colors"
        data-testid="carousel-next"
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-32 lg:bottom-40 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`w-12 h-1 transition-all ${
              index === selectedIndex ? 'bg-safety-orange' : 'bg-white/30 hover:bg-white/50'
            }`}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </div>

      {/* Tracking Widget */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 w-full max-w-2xl px-4">
        <div className="tracking-widget p-4 lg:p-6" data-testid="tracking-widget">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">
                Track Your Shipment
              </label>
              <Input
                type="text"
                placeholder="Enter tracking number (e.g., RSL123456)"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                className="h-14 text-lg border-2 border-slate-200 focus:border-safety-orange"
                data-testid="tracking-input"
              />
            </div>
            <Link
              to={trackingId ? `/tracking?id=${trackingId}` : '/tracking'}
              className="flex-shrink-0"
            >
              <Button
                className="w-full sm:w-auto h-14 px-8 bg-navy hover:bg-navy/90 text-white font-bold uppercase tracking-widest text-sm"
                data-testid="tracking-submit-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Track
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
